import {
  CategoryStandardProduct,
  StandardProductDeliveryCondition,
} from "shared/api/middleware-api-client";
import {
  REACT_APP_ESD_WAREHOUSE_ID,
  REACT_APP_VILLAARENA_WAREHOUSE_ID,
} from "shared/config";

export interface StandardProductAviability {
  delivery: StandardProductDeliveryCondition | undefined;
  pickUp: StandardProductPickUpAviabilities;
}

interface StandardProductPickUpAviabilities {
  amsterdam: StandardProductDeliveryCondition | undefined;
  vlaardingen: StandardProductDeliveryCondition | undefined;
}

export const useStandardProductAvailability = () => {
  const getStandardProductAvailability = (
    standardProduct: CategoryStandardProduct,
  ): StandardProductAviability => {
    return {
      delivery: getStandardProductDeliveryAvailability(standardProduct),
      pickUp: getStandardProductPickUpAvailability(standardProduct),
    };
  };

  const getStandardProductsAviability = (
    standardProducts: CategoryStandardProduct[],
  ): StandardProductAviability => {
    return {
      delivery:
        getStandardProductsHighestDeliveryAvailability(standardProducts),
      pickUp: getStandardProductHighestPickUpAvailability(standardProducts),
    };
  };

  const getStandardProductHighestPickUpAvailability = (
    standardProducts: CategoryStandardProduct[],
  ): StandardProductPickUpAviabilities => {
    const storesAvailableForAllStandardProductsPickUp = standardProducts.reduce(
      (prevStores: StandardProductPickUpAviabilities, standardProduct) => {
        const resultingPickUpStores =
          getStandardProductPickUpAvailability(standardProduct);

        if (
          prevStores.amsterdam &&
          prevStores.amsterdam.conditionType === "noStock"
        ) {
          resultingPickUpStores.amsterdam = prevStores.amsterdam;
        }
        if (
          prevStores.vlaardingen &&
          prevStores.vlaardingen.conditionType === "noStock"
        ) {
          resultingPickUpStores.vlaardingen = prevStores.vlaardingen;
        }

        return resultingPickUpStores;
      },
      {
        amsterdam: undefined,
        vlaardingen: undefined,
      },
    );

    return storesAvailableForAllStandardProductsPickUp;
  };

  const getStandardProductPickUpAvailability = (
    standardProduct: CategoryStandardProduct,
  ): StandardProductPickUpAviabilities => {
    const pickUpAvailabilities: StandardProductPickUpAviabilities = {
      amsterdam: standardProduct.deliveryCondition
        ? standardProduct.deliveryCondition.find(
            (condition) => condition.conditionType === "noStock",
          )
        : undefined,
      vlaardingen: standardProduct.deliveryCondition
        ? standardProduct.deliveryCondition.find(
            (condition) => condition.conditionType === "noStock",
          )
        : undefined,
    };

    if (!standardProduct.exactArticleWarehouseLocation) {
      // TODO: show error;

      return pickUpAvailabilities;
    }

    // Find all delivery options placed in Amsterdam
    const villaArenaWarehouses =
      standardProduct.exactArticleWarehouseLocation.filter(
        (warehouse) =>
          warehouse.warehouseCode === REACT_APP_VILLAARENA_WAREHOUSE_ID,
      );

    // Find all delivery options placed in Vlaardingen
    const ESDWarehouses = standardProduct.exactArticleWarehouseLocation.filter(
      (warehouse) => warehouse.warehouseCode === REACT_APP_ESD_WAREHOUSE_ID,
    );

    // Initialize as a null. If nothing is found, the delivery will remain as default ( 21 days)
    let villaArenaWarehouseTotalStock = null;
    // Add the total stock from the all the subsections
    if (villaArenaWarehouses.length > 0) {
      villaArenaWarehouseTotalStock = villaArenaWarehouses.reduce(
        (accumulator, warehouse) => accumulator + warehouse.itemStock,
        0, // Initial value of the accumulator
      );
    }

    // Initialize as a null. If nothing is found, the delivery will remain as default ( 21 days)
    let ESDWarehouseTotalStock = null;
    // Add the total stock from the all the subsections
    if (ESDWarehouses.length > 0) {
      ESDWarehouseTotalStock = ESDWarehouses.reduce(
        (accumulator, warehouse) => accumulator + warehouse.itemStock,
        0, // Initial value of the accumulator
      );
    }

    // Check if the stock items have been found +
    //   Check if there's enough stock instantly

    if (
      villaArenaWarehouseTotalStock &&
      standardProduct.amount <= villaArenaWarehouseTotalStock
    ) {
      pickUpAvailabilities.amsterdam = standardProduct.deliveryCondition.find(
        (condition) => condition.conditionType === "productInStock",
      );
    }

    // Check if the stock items have been found +
    //   Check if there's enough stock instantly
    if (
      ESDWarehouseTotalStock &&
      standardProduct.amount <= ESDWarehouseTotalStock
    ) {
      pickUpAvailabilities.vlaardingen = standardProduct.deliveryCondition.find(
        (condition) => condition.conditionType === "productInStock",
      );
    }

    return pickUpAvailabilities;
  };

  const getStandardProductsHighestDeliveryAvailability = (
    standardProducts: CategoryStandardProduct[],
  ): StandardProductDeliveryCondition | undefined => {
    const highestDeliveryAvailability = standardProducts.reduce(
      (
        prevHighest: StandardProductDeliveryCondition | undefined,
        standardProduct: CategoryStandardProduct,
      ) => {
        const deliveryAvailability =
          getStandardProductDeliveryAvailability(standardProduct);

        return getHighestDeliveryAvailability(
          deliveryAvailability,
          prevHighest,
        );
      },
      undefined,
    );

    return highestDeliveryAvailability;
  };

  const getStandardProductDeliveryAvailability = (
    standardProduct: CategoryStandardProduct,
  ): StandardProductDeliveryCondition | undefined => {
    // Find the delivery condition with conditionType "noStock"
    const noStockCondition = standardProduct.deliveryCondition.find(
      (condition) => condition.conditionType === "noStock",
    );
    // Find the delivery condition with conditionType "productInStock"
    const inStockCondition = standardProduct.deliveryCondition.find(
      (condition) => condition.conditionType === "productInStock",
    );

    // Check if the exactArticleWarehouseLocation is not available
    if (!standardProduct.exactArticleWarehouseLocation) {
      // Return the noStockCondition if the exactArticleWarehouseLocation is not available
      return noStockCondition;
    }

    // Find all delivery options placed in Vlaardingen
    const ESDWarehouses = standardProduct.exactArticleWarehouseLocation.filter(
      (warehouse) => warehouse.warehouseCode === REACT_APP_ESD_WAREHOUSE_ID,
    );

    // Initialize as a null. If nothing is found, the delivery will remain as default ( 21 days)
    let ESDWarehouseTotalStock = null;
    // Add the total stock from the all the subsections
    if (ESDWarehouses.length > 0) {
      ESDWarehouseTotalStock = ESDWarehouses.reduce(
        (accumulator, warehouse) => accumulator + warehouse.itemStock,
        0, // Initial value of the accumulator
      );
    }

    // Check if the stock items have been found +
    //   Check if there's enough stock instantly
    if (
      ESDWarehouseTotalStock &&
      standardProduct.amount <= ESDWarehouseTotalStock
    ) {
      return inStockCondition;
    }

    // Return the noStockCondition if the stock items have not been found
    return noStockCondition;
  };

  const getHighestDeliveryAvailability = (
    a: StandardProductDeliveryCondition | undefined,
    b: StandardProductDeliveryCondition | undefined,
  ) => {
    if (!b || (a && a.deliveryCondition.days > b.deliveryCondition.days)) {
      return a;
    }
    return b;
  };

  const isReceiveMethodAvailable = (
    condition: StandardProductDeliveryCondition | undefined,
  ) => {
    return !!condition && condition.conditionType === "productInStock";
  };

  const getReceiveMethodAvailability = (
    method: string,
    standardProducts: CategoryStandardProduct[],
    withAssembly = false,
  ): StandardProductDeliveryCondition | undefined => {
    const methodAviability = getStandardProductsAviability(standardProducts);

    if (!method && !withAssembly) return methodAviability.delivery;

    if (!method && withAssembly) return methodAviability.delivery;

    if (method === "ESD") return methodAviability.pickUp.vlaardingen;

    if (method === "VillaArena") return methodAviability.pickUp.amsterdam;
  };

  return {
    getStandardProductAvailability,
    getStandardProductsAviability,
    isReceiveMethodAvailable,
    getReceiveMethodAvailability,
  };
};
